<script>
    import {onMount} from 'svelte';
    import {t} from '$utils/lang.js';
    import {devLog as _devLog} from '$utils/devLog.js';
    import {loadStripeJs} from '$lib/stripeLoader.js';
    import {query} from '$lib/graphqlClient.js';
    import {store as basketStore} from '$lib/basketStore.svelte.js';
    import {paymentStore, cancelStripeExpress} from '$lib/paymentStore.svelte.js';
    const devLog = (tag, ...args) => _devLog(`StripeExpress:${tag}`, ...args);

    // reference: [1]
    let {paymentId, metadata} = $props();

    let containerEl;
    let visible = $state(false);
    let errorMessage = $state(null);
    let expressElement = null;

    const RATES_QUERY = `query ExpressRates($country: String!) { expressShippingRates(country: $country) { id title cost } }`;
    const SETUP_QUERY = `query ExpressSetup { marketCountry { code } countries { code } }`;

    /** Tell the server why Stripe.js refused (no personal data), so the log shows what the customer saw. */
    function reportError(stage, err, wallets) {
        fetch('/index.php?cl=m7-stripe-express&fnc=report', {
            method: 'POST',
            credentials: 'include',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({stage, code: err?.code, type: err?.type, declineCode: err?.decline_code, wallets}),
        }).catch(() => {});
    }

    const minor = (value) => Math.round(Number(value) * 100);

    /** Basket total without shipping: what the wallet adds the chosen shipping rate to. */
    function itemsMinor() {
        const snap = basketStore.snapshot;
        if (!snap?.total) return null;
        return minor(snap.total.brutto) - minor(snap.shipping?.brutto ?? 0);
    }

    const toWalletRates = (rates) => rates.map(r => ({id: r.id, displayName: r.title, amount: minor(r.cost)}));

    async function fetchRates(country) {
        const data = await query(RATES_QUERY, {country});
        return data?.expressShippingRates ?? [];
    }

    onMount(() => {
        let destroyed = false;

        (async () => {
            try {
                // The basket may still be loading when the express block mounts: wait for its snapshot (up to 10 s).
                for (let i = 0; i < 40 && itemsMinor() === null && !destroyed; i++) {
                    await new Promise(r => setTimeout(r, 250));
                }
                if (destroyed) return;
                const base = itemsMinor();
                if (base === null || base <= 0) { reportError('init-skip', {code: 'no_basket_amount'}); return; }
                const setup = await query(SETUP_QUERY);
                const defaultCountry = setup?.marketCountry?.code ?? 'DE';
                const allowed = (setup?.countries ?? []).map(c => c.code).filter(Boolean);
                const initialRates = toWalletRates(await fetchRates(defaultCountry));
                if (initialRates.length === 0) { reportError('init-skip', {code: 'no_shipping_rates'}); return; }

                await loadStripeJs();
                if (destroyed) return;

                const currency = String(metadata.currency ?? 'EUR').toLowerCase();
                const stripe = window.Stripe(metadata.publishableKey, {locale: metadata.locale ?? 'auto'});
                const elements = stripe.elements({
                    mode: 'payment',
                    amount: base + initialRates[0].amount,
                    currency,
                    captureMethod: 'manual',
                    // Must equal the types of the PaymentIntent (PHP PaymentMethods::stripeTypes), else Stripe refuses the confirmation.
                    paymentMethodTypes: ['card', 'link'],
                    locale: metadata.locale ?? 'auto',
                });
                expressElement = elements.create('expressCheckout', {
                    buttonHeight: 42,
                    paymentMethods: {applePay: 'auto', googlePay: 'auto', link: 'auto', paypal: 'never', amazonPay: 'never', klarna: 'never'},
                });

                let currentRateAmount = initialRates[0].amount;
                const setAmount = (rateAmount) => {
                    currentRateAmount = rateAmount;
                    elements.update({amount: base + rateAmount});
                };

                // No wallet available on this device/browser: the component stays hidden.
                expressElement.on('ready', ({availablePaymentMethods}) => {
                    visible = !!availablePaymentMethods;
                    reportError('ready', null, availablePaymentMethods ?? {});
                    devLog('ready', {availablePaymentMethods});
                });

                expressElement.on('click', (event) => {
                    errorMessage = null;
                    event.resolve({
                        emailRequired: true,
                        phoneNumberRequired: true,
                        shippingAddressRequired: true,
                        allowedShippingCountries: allowed.length ? allowed : undefined,
                        shippingRates: initialRates,
                    });
                });

                expressElement.on('shippingaddresschange', async (event) => {
                    try {
                        const rates = toWalletRates(await fetchRates(event.address.country));
                        if (rates.length === 0) {
                            event.reject();
                            return;
                        }
                        setAmount(rates[0].amount);
                        event.resolve({shippingRates: rates});
                    } catch (err) {
                        devLog('shippingaddresschange:error', {error: err.message});
                        event.reject();
                    }
                });

                expressElement.on('shippingratechange', (event) => {
                    setAmount(event.shippingRate.amount);
                    event.resolve();
                });

                expressElement.on('confirm', async (event) => {
                    const fail = (message) => {
                        errorMessage = message;
                        event.paymentFailed({reason: 'fail'});
                    };
                    try {
                        reportError('confirm-event', {code: 'received'});
                        const {error: submitError} = await elements.submit();
                        if (submitError) { reportError('submit', submitError); return fail(submitError.message); }

                        const ship = event.shippingAddress ?? {};
                        const res = await fetch(`/index.php?${new URLSearchParams({cl: 'm7-checkout-dpc', fnc: 'approveExpressOrder', payment_id: paymentId})}`, {
                            method: 'POST',
                            headers: {'Content-Type': 'application/json'},
                            body: JSON.stringify({
                                email: event.billingDetails?.email,
                                name: ship.name ?? event.billingDetails?.name,
                                phone: event.billingDetails?.phone,
                                address: ship.address,
                                shippingRateId: event.shippingRate?.id,
                                amount: base + currentRateAmount,
                            }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok || !data?.clientSecret) {
                            devLog('approve:error', {status: res.status, message: data?.message});
                            if (data?.message === 'login_required') return fail(t('Zu dieser E-Mail-Adresse gibt es ein Kundenkonto. Bitte melde dich an, um fortzufahren.'));
                            if (data?.message === 'amount_mismatch') return fail(t('Der Betrag hat sich geändert. Bitte versuche es erneut.'));
                            return fail(t('Die Zahlung konnte nicht gestartet werden. Bitte versuche es erneut.'));
                        }

                        const returnUrl = `${window.location.origin}${window.location.pathname}`;
                        const {error} = await stripe.confirmPayment({
                            elements,
                            clientSecret: data.clientSecret,
                            confirmParams: {return_url: returnUrl},
                            redirect: 'if_required',
                        });
                        if (error) {
                            reportError('confirm', error);
                            // Release the reservation of this attempt; a new click creates a fresh one.
                            cancelStripeExpress('confirm_error');
                            return fail(error.message || t('Die Zahlung wurde nicht abgeschlossen. Bitte versuche es erneut oder wähle eine andere Zahlungsart.'));
                        }

                        reportError('confirm-ok', {code: 'authorized'});
                        paymentStore.stripeExpressLockedAmount = Number(data.lockedAmount);
                        paymentStore.expressApproved = true;
                        devLog('approved', {intentId: data.intentId, lockedAmount: data.lockedAmount});
                    } catch (err) {
                        devLog('confirm:error', {error: err.message});
                        fail(t('Die Zahlung konnte nicht gestartet werden. Bitte versuche es erneut.'));
                    }
                });

                expressElement.mount(containerEl);
            } catch (err) {
                devLog('init:error', {error: err.message});
                reportError('init-error', {code: 'init_error'});
            }
        })();

        return () => {
            destroyed = true;
            try { expressElement?.destroy(); } catch { /* noop */ }
        };
    });
</script>

<div class="w-full" class:hidden={!visible} data-checkout-section="stripe-express" data-payment-id={paymentId}>
    <div bind:this={containerEl}></div>
    {#if errorMessage}
        <p class="text-scarlet text-sm mt-1" data-checkout-section="stripe-express-error">{errorMessage}</p>
    {/if}
</div>
